import { createContext, useContext, useMemo, type ReactNode } from 'react';
import * as RadixDirection from 'radix-ui/direction';
import { createTranslator, type LocaleCode, type Translator } from '@kidova/i18n';

const UIContext = createContext<Translator | null>(null);
const fallback = createTranslator('en');

export interface UIProviderProps {
  locale: LocaleCode;
  children: ReactNode;
}

/**
 * Supplies the locale, text direction and translator to every component. Tooltips and the
 * Toaster bring their own Radix providers and the Combobox its React Aria locale, so none of that
 * code loads until they render.
 * Put it once at the app root, next to `<html lang dir>`.
 */
export function UIProvider({ locale, children }: UIProviderProps) {
  const translator = useMemo(() => createTranslator(locale), [locale]);
  return (
    <UIContext.Provider value={translator}>
      <RadixDirection.Provider dir={translator.dir}>{children}</RadixDirection.Provider>
    </UIContext.Provider>
  );
}

/** The active translator; English outside a provider so components never crash in isolation. */
export function useUI(): Translator {
  return useContext(UIContext) ?? fallback;
}
